import { browserTime } from 'utils/time'
import React, { useState } from 'react'
import useSWR from 'swr'
import {
  Alert,
  Box,
  Pagination,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { Loader, Offers } from 'components'
import OfferDisplayToggle from 'components/offers/OfferDisplayToggle'
import useOfferDisplayMode from 'components/offers/useOfferDisplayMode'
import api from 'utils/api'
import AccountPage from '../../components/AccountPage'
import ClaimFeedback from './ClaimFeedback'
import AccountPageIntro from '../../components/AccountPageIntro'

const ClaimedPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [activeOnly, setActiveOnly] = useState(true)
  const [displayMode, setDisplayMode] = useOfferDisplayMode()
  const apiRoute = `/user/claims?active=${activeOnly}&size=12&page=${currentPage - 1}`
  const { data, error, mutate } = useSWR(apiRoute, () => api.get(apiRoute), {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  })

  const handleAvailabilityChange = (_event, value) => {
    setActiveOnly(value === 'available')
    setCurrentPage(1)
  }

  if (error) {
    return (
      <AccountPage title="Claimed">
        <Box p={3}>
          <Alert severity="error">Your claimed offers could not be loaded. Try again later.</Alert>
        </Box>
      </AccountPage>
    )
  }

  if (!data) {
    return (
      <AccountPage title="Claimed">
        <Box p={4} display="flex" justifyContent="center">
          <Loader />
        </Box>
      </AccountPage>
    )
  }

  return (
    <AccountPage title="Claimed">
      <Box px={{ xs: 2, md: 3 }} pb={4}>
        <AccountPageIntro
          action={<OfferDisplayToggle displayMode={displayMode} onChange={setDisplayMode} />}
        >
          Offers you claimed in the last three months.
        </AccountPageIntro>
        <Tabs
          aria-label="Claimed offer availability"
          onChange={handleAvailabilityChange}
          value={activeOnly ? 'available' : 'unavailable'}
          sx={{
            mb: 1,
            minHeight: 34,
            width: 'fit-content',
            '& .MuiTabs-flexContainer': { gap: 2 },
            '& .MuiTabs-indicator': {
              borderRadius: 2,
              height: 2,
            },
            '& .MuiTab-root': {
              fontWeight: 500,
              minHeight: 34,
              minWidth: 'auto',
              px: 0.5,
              py: 0.5,
              textTransform: 'none',
              whiteSpace: 'nowrap',
            },
            '& .MuiTab-root.Mui-selected': {
              color: 'primary.main',
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Available" value="available" sx={{ fontSize: 14 }} />
          <Tab label="Unavailable" value="unavailable" sx={{ fontSize: 14 }} />
        </Tabs>
        {data.content.length ? (
          <Offers
            offers={data.content}
            offerOrigin="claimed"
            offerType={displayMode}
            columns={4}
            getOffer={(claim) => claim.offer}
            getOfferKey={(claim) => claim.snapshotId || claim.claimId}
            renderItem={(claim, offer, effectiveDisplayMode) => {
              const isList = effectiveDisplayMode === 'list'
              const claimedTime = (
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75, maxWidth: '100%', minWidth: 0 }}>
                  <Box
                    aria-hidden="true"
                    component="span"
                    sx={{
                      bgcolor: activeOnly ? 'success.main' : 'text.disabled',
                      borderRadius: '50%',
                      flexShrink: 0,
                      height: 7,
                      width: 7,
                    }}
                  />
                  <Typography
                    color={activeOnly ? 'primary.main' : 'text.secondary'}
                    fontSize={11}
                    fontWeight={600}
                    letterSpacing="0.035em"
                    sx={{ overflowWrap: 'anywhere' }}
                    textTransform="uppercase"
                  >
                    Claimed {browserTime(claim.claimedAt).format('MMM D, YYYY [at] h:mm A')}
                  </Typography>
                </Box>
              )
              const actions = <ClaimFeedback claim={claim} onSubmitted={mutate} />
              return (
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: '#F7FAFC',
                    border: '1px solid',
                    borderColor: 'rgba(14, 117, 189, 0.14)',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: isList ? 'row' : 'column',
                    maxWidth: isList ? 1080 : undefined,
                    mx: isList ? 'auto' : undefined,
                    p: 0.75,
                    width: '100%',
                  }}
                >
                  {isList ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flex: '0 0 210px',
                        flexDirection: 'column',
                        justifyContent: 'space-around',
                        minWidth: 0,
                        pl: 1,
                        pr: 1,
                        py: 0.75,
                      }}
                    >
                      {claimedTime}
                      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row' }}>{actions}</Box>
                    </Box>
                  ) : (
                    <Box sx={{ px: 1, py: 0.75 }}>{claimedTime}</Box>
                  )}
                  {isList ? (
                    <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>{offer}</Box>
                  ) : (
                    <Box sx={{ alignItems: 'stretch', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', flex: '1 1 260px', minWidth: 0 }}>{offer}</Box>
                      <Box sx={{ alignItems: 'center', alignSelf: 'flex-end', display: 'flex', flex: '0 0 36px', flexDirection: 'column', ml: 'auto' }}>
                        {actions}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )
            }}
          />
        ) : (
          <Paper
            variant="outlined"
            sx={{
              bgcolor: '#F7FAFC',
              borderColor: 'rgba(14, 117, 189, 0.14)',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" gutterBottom>
              {activeOnly ? 'No available claimed offers' : 'No unavailable claimed offers'}
            </Typography>
            <Typography color="text.secondary">
              Offers you claim will stay here for three months.
            </Typography>
          </Paper>
        )}
        {data.totalPages > 1 && (
          <Box display="flex" justifyContent="center" pt={4}>
            <Pagination
              page={currentPage}
              count={data.totalPages}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </AccountPage>
  )
}

export default ClaimedPage
